import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Container from "../../../components/Container";
import { useNavigate, useParams } from "react-router-dom";
import { IBlogRecord, IBlogStatus, Status } from "../../../lib/types";
import { Record } from "pocketbase";
import pb from "../../../lib/pocketbase";
import Loading from "../../../components/Loading";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import Button from "../../../components/form/button";
import MarkdownEditor from "../../../components/form/MarkdownEditor";
import BackButton from "../../../components/form/BackButton";
import { config } from "../../../config";
import { ClientResponseError } from "pocketbase";
import { useToast } from "../../../components/ui/use-toast";

export default function EditBlogPost() {
    const id = useParams().id;
    const { toast } = useToast();
    const navigate = useNavigate();
    const [saveStatus, setSaveStatus] = useState<Status>("none");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [post, setPost] = useState<IBlogRecord>();
    const [title, setTitle] = useState("");
    const [status, setStatus] = useState("Draft");
    const [published, setPublished] = useState(new Date().toISOString());
    const [banner, setBanner] = useState<File>();
    const [content, setContent] = useState("");

    async function load() {
        if (!id) return;
        const bp: IBlogRecord = await pb.collection("blog").getOne(id);
        setPost(bp);
        setTitle(bp.title);
        setStatus(bp.status);
        setPublished(bp.published);
        setContent(bp.content);

        setIsLoading(false);
    }

    async function save() {
        if (!id) return;
        setSaveStatus("loading");

        const fd = new FormData();
        fd.append("title", title);
        fd.append("status", status);
        console.log(published);
        fd.append("published", published);
        fd.append("content", content);
        if (banner) fd.append("banner", banner);

        pb.collection("blog")
            .update(id, fd)
            .then(() => {
                setTimeout(() => {
                    setSaveStatus("success");
                    navigate("/studio/blog");
                }, 500);
            })
            .catch((error: ClientResponseError) => {
                setSaveStatus("error");
                setError(error?.response?.message);
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 2000);
            });
    }

    function removeBanner() {
        if (!id) return;
        const fd = new FormData();
        fd.append("banner", "");
        if (pb.authStore.model) {
            pb.collection("blog")
                .update(id, fd)
                .then((b) => {
                    toast({
                        title: `👍 Removed banner`,
                        description: `Successfully removed the banner of this post`
                    });
                    const blogRecord = b as IBlogRecord;
                    setPost(blogRecord);
                });
        }
    }

    useEffect(() => {
        load();
    }, []);

    function statusClasses(status: string) {
        if (status === "Draft") return "bg-orange-500";
        if (status === "Scheduled") return "bg-indigo-500";
        if (status === "Public") return "bg-blue-500";
    }
    const bannerImage = config.pocketbase + "/api/files/" + post?.collectionId + "/" + post?.id + "/" + post?.banner;
    return (
        <>
            <Helmet>
                <title>ReinVB Studio — Blog — Edit</title>
            </Helmet>
            <Container className="mb-8 mt-4 md:mt-8 lg:mt-16">
                {isLoading ? (
                    <div className="flex w-full items-center justify-center">
                        <Loading />
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <BackButton />
                        <div className="flex flex-col gap-2">
                            <h3>Post title</h3>
                            <Input value={title} placeholder="Post title" onChange={(e) => setTitle(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                                <h3>Published date</h3>
                                <Input className="" type="date" defaultValue={new Date(published)?.toISOString()?.substring(0, 10)} onChange={(e) => setPublished(e.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2">
                                <h3>Post status</h3>
                                <Select value={status} onValueChange={(v) => setStatus(v)}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Draft">
                                            <div className={`rounded-lg px-2 py-1 text-sm text-white ${statusClasses("Draft")}`}>Draft</div>
                                        </SelectItem>
                                        <SelectItem value="Scheduled">
                                            <div className={`rounded-lg px-2 py-1 text-sm text-white ${statusClasses("Scheduled")}`}>Scheduled</div>
                                        </SelectItem>
                                        <SelectItem value="Public">
                                            <div className={`rounded-lg px-2 py-1 text-sm text-white ${statusClasses("Public")}`}>Public</div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="flex justify-between">
                                Banner
                                <div className="flex items-center gap-2">
                                    {post?.banner && <img src={bannerImage} alt="Banner" className="h-8 w-12 rounded-lg" />}
                                    <Button size={"extraSmall"} type="button" onClick={removeBanner}>
                                        Remove
                                    </Button>
                                </div>
                            </div>
                            <Input
                                className="file:text-slate-400 dark:file:text-slate-500"
                                id="small_size"
                                type="file"
                                onChange={(e) => {
                                    const file = e.target.files?.[0]; // Access the first file from the FileList object
                                    if (file) {
                                        setBanner(file); // Assign the file to the "avatar" field in the form data using setValue
                                    }
                                }}></Input>
                        </div>
                        <div className="flex flex-col gap-2">
                            <h3>Content</h3>
                            <MarkdownEditor value={content} onChange={(c) => setContent(c)} className="h-80" markdownClassName="h-fit" placeholder="Post content in markdown" />
                        </div>
                        <Button variant={"dark"} status={saveStatus} className="w-fit" onClick={save}>
                            {saveStatus === "loading" && "Saving"}
                            {saveStatus === "success" && "Saved"}
                            {saveStatus === "error" && "Error"}
                            {saveStatus === "none" && "Save"}
                        </Button>
                    </div>
                )}
            </Container>
        </>
    );
}
